/**
 * Results Stage Component
 * Final stage showing session performance and feedback
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { QualityTaskOutcome } from '../quality/QualityTaskOutcome';
import {
    Search,
    Target,
    CheckCircle,
    AlertTriangle,
    Award,
    Brain,
    RefreshCw,
    Trophy,
    Star,
    Sparkles,
    TrendingUp,
    Eye
} from 'lucide-react';
import SessionReplay from './SessionReplay';

// Badge icons mapping
const BADGE_ICONS = {
    'trophy': Trophy,
    'star': Star,
    'award': Award,
    'medal': Award,
    'crown': Award,
    'check-circle': CheckCircle,
    'zap': Sparkles,
    'flame': Sparkles,
    'sparkles': Sparkles,
    'target': Target,
    'trending-up': TrendingUp,
    'calendar': Award,
    'shield': Award,
    'gem': Sparkles,
    'globe': Award,
    'search': Search
};

const BadgeIcon = ({ icon, className }) => {
    const IconComponent = BADGE_ICONS[icon] || Award;
    return <IconComponent className={className} />;
};

const ResultsStage = ({ session }) => {
    const navigate = useNavigate();
    const [showReplay, setShowReplay] = useState(false);
    const scores = session.scores || {};
    const feedback = session.feedback || {};
    const newBadges = session.new_badges_earned || [];
    
    // R-PAT Phase 6 qualifying threshold
    const PASSING_SCORE = 70;
    const overallScore = scores.overall || 0;
    const passed = overallScore >= PASSING_SCORE;

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-500';
        if (score >= 60) return 'text-amber-500';
        return 'text-red-500';
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    Session Results
                </CardTitle>
                <CardDescription>
                    Here&apos;s how you performed in this sourcing exercise
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* New Badges Earned */}
                {newBadges.length > 0 && (
                    <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 animate-pulse">
                        <h4 className="font-semibold mb-3 flex items-center gap-2 text-amber-600">
                            <Trophy className="h-5 w-5" />
                            New Badges Earned!
                        </h4>
                        <div className="flex flex-wrap gap-3">
                            {newBadges.map((badge, idx) => (
                                <div 
                                    key={idx}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background border"
                                >
                                    <div className="p-1.5 rounded-lg bg-amber-500/20">
                                        <BadgeIcon icon={badge.icon} className="h-4 w-4 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm">{badge.name}</p>
                                        <p className="text-xs text-muted-foreground">{badge.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Overall Score with Pass/Fail Indicator */}
                <div className={`text-center p-6 rounded-xl ${
                    passed 
                        ? 'bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30'
                        : 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30'
                }`}>
                    <p className="text-sm text-muted-foreground mb-2">Overall Score</p>
                    <p className={`text-5xl font-bold ${getScoreColor(overallScore)}`}>
                        {overallScore}%
                    </p>
                    
                    {/* Pass/Fail Badge */}
                    <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                        passed 
                            ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                            : 'bg-amber-500/20 text-amber-700 dark:text-amber-400'
                    }`}>
                        {passed ? (
                            <>
                                <CheckCircle className="h-5 w-5" />
                                <span className="font-semibold">Passed! (Threshold: {PASSING_SCORE}%)</span>
                            </>
                        ) : (
                            <>
                                <AlertTriangle className="h-5 w-5" />
                                <span className="font-semibold">Below Threshold ({PASSING_SCORE}% required)</span>
                            </>
                        )}
                    </div>
                    
                    {/* Retake Message for Failed Attempts */}
                    {!passed && (
                        <div className="mt-4 p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm">
                            <p className="font-medium mb-1">Need to improve your score?</p>
                            <p className="text-amber-700 dark:text-amber-300">
                                For R-PAT Phase 6 certification, you need at least {PASSING_SCORE}% to pass this lab. 
                                Click &quot;Retake This Lab&quot; below to try again with the same job brief, or try a different lab.
                            </p>
                        </div>
                    )}
                    
                    {/* Success Message for Passed Attempts */}
                    {passed && (
                        <div className="mt-4 p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 text-sm">
                            <p className="font-medium">
                                Great job! This lab counts towards your R-PAT Phase 6 progress.
                            </p>
                        </div>
                    )}
                </div>

                {/* Score Breakdown */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                        <Search className="h-6 w-6 mx-auto mb-2 text-amber-500" />
                        <p className={`text-2xl font-bold ${getScoreColor(scores.search_string)}`}>
                            {scores.search_string || 0}%
                        </p>
                        <p className="text-xs text-muted-foreground">Search String</p>
                    </div>
                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                        <Target className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
                        <p className={`text-2xl font-bold ${getScoreColor(scores.selection_accuracy)}`}>
                            {scores.selection_accuracy || 0}%
                        </p>
                        <p className="text-xs text-muted-foreground">Selection Accuracy</p>
                    </div>
                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                        <Brain className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                        <p className={`text-2xl font-bold ${getScoreColor(scores.reasoning_quality)}`}>
                            {scores.reasoning_quality || 0}%
                        </p>
                        <p className="text-xs text-muted-foreground">Reasoning Quality</p>
                    </div>
                </div>

                {/* Additional Metrics */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg bg-muted/30 text-center">
                        <p className={`text-xl font-bold ${getScoreColor(scores.industry_awareness)}`}>
                            {scores.industry_awareness || 0}%
                        </p>
                        <p className="text-xs text-muted-foreground">Industry Awareness</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30 text-center">
                        <p className={`text-xl font-bold ${getScoreColor(scores.context_understanding)}`}>
                            {scores.context_understanding || 0}%
                        </p>
                        <p className="text-xs text-muted-foreground">Context Understanding</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30 text-center">
                        <p className={`text-xl font-bold ${getScoreColor(scores.critical_thinking)}`}>
                            {scores.critical_thinking || 0}%
                        </p>
                        <p className="text-xs text-muted-foreground">Critical Thinking</p>
                    </div>
                </div>

                {/* Selection Results */}
                <div className="p-4 rounded-xl bg-muted/30">
                    <h4 className="font-semibold mb-2">Selection Results</h4>
                    <p className="text-sm text-muted-foreground" data-testid="selection-results-text">
                        You correctly identified {feedback.correct_count || 0} out of {feedback.total_evaluated || 0} candidates.
                        {feedback.close_count > 0 && ` ${feedback.close_count} more earned partial credit for close or well-reasoned calls.`}
                    </p>
                </div>

                {/* Strengths */}
                {feedback.strengths?.length > 0 && (
                    <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/30">
                        <h4 className="font-semibold mb-2 flex items-center gap-2 text-emerald-600">
                            <CheckCircle className="h-4 w-4" />
                            Your Strengths
                        </h4>
                        <ul className="space-y-1 text-sm">
                            {feedback.strengths.map((s, idx) => (
                                <li key={idx}>• {s}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Blind Spots */}
                {feedback.blind_spots?.length > 0 && (
                    <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/30">
                        <h4 className="font-semibold mb-2 flex items-center gap-2 text-amber-600">
                            <AlertTriangle className="h-4 w-4" />
                            Areas to Improve
                        </h4>
                        <ul className="space-y-1 text-sm">
                            {feedback.blind_spots.map((s, idx) => (
                                <li key={idx}>• {s}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Detailed Feedback */}
                {feedback.detailed_feedback && (
                    <div className="p-4 rounded-xl border">
                        <h4 className="font-semibold mb-2">Detailed Feedback</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {feedback.detailed_feedback}
                        </p>
                    </div>
                )}

                {/* Actions */}
                <QualityTaskOutcome kind="sourcing_lab" refKey={session.lab_id} attemptScore={overallScore}>
                <div className="flex flex-col gap-3 pt-4 border-t">
                    {/* Retake button shown prominently when failed */}
                    {!passed && (
                        <Button 
                            onClick={() => {
                                // Navigate to start a new session with same lab
                                navigate(`/sourcing-labs?retake=${session.lab_id || session.sourcing_lab_id || ''}`);
                            }}
                            className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                            data-testid="retake-lab-btn"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retake This Lab (Need {PASSING_SCORE}%+)
                        </Button>
                    )}
                    
                    <div className="flex gap-3">
                        <Button 
                            variant="outline"
                            onClick={() => setShowReplay(true)}
                            className="flex-1"
                            data-testid="review-session-btn"
                        >
                            <Eye className="h-4 w-4 mr-2" />
                            Review Session
                        </Button>
                        <Button 
                            onClick={() => navigate('/sourcing-labs')}
                            className={`flex-1 ${passed ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                            variant={passed ? "default" : "outline"}
                            data-testid="try-another-lab"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {passed ? 'Continue to Next Lab' : 'Try Another Lab'}
                        </Button>
                    </div>
                </div>
                </QualityTaskOutcome>
                
                {/* Session Replay Modal */}
                <SessionReplay 
                    session={session} 
                    open={showReplay} 
                    onClose={() => setShowReplay(false)} 
                />
            </CardContent>
        </Card>
    );
};

export default ResultsStage;
